import { useEffect, useRef, useState } from 'react';
import { COPY, programLabel } from '../content';
import { apiGet, apiPost, type ApplicationItem, type Ownership } from '../api';
import { useAsync } from '../hooks';
import { shortDate, pluralize } from '../format';
import { getCreds } from '../session';
import { Spinner, LoadError, Empty, Pill, Sheet, Toast } from '../components/ui';

const PROGRAMS = ['brand_ambassador', 'demo_center', 'pro_content_creator', 'inflatable_boarder'];
const QUALIFYING = ['ISUP', 'SUP', 'Surf'];

type Tab = 'needs_review' | 'auto_approved' | 'decided';
type OwnershipState = 'board_found' | 'verified' | 'proof_to_confirm' | 'no_board';

// One-line explanations for each fraud chip (hover title + tap line).
const FRAUD_EXPLAIN: Record<string, string> = {
  gmail_duplicate: 'Another applicant used the same Gmail (dots and + are ignored).',
  invalid_phone: 'The phone number did not pass validation.',
  country_outside_na: 'The stated country is outside the US and Canada.',
  coupon_wording: 'Coupon or deal words appear in the name, handle or pitch.',
  third_party_handle: 'The social handle matches a known deal site.',
};

function qualifyingOrder(ownership: Ownership | null): { name: string; board: string; dateIso: string } | null {
  const orders = (ownership as unknown as { orders?: any[] })?.orders;
  if (!Array.isArray(orders)) return null;
  for (const o of orders) {
    const board = (o.lineItems ?? []).find((li: any) => QUALIFYING.includes(li.productType));
    if (o.paid && !o.fullyRefunded && board) {
      return { name: o.name, board: board.title, dateIso: o.processedAtIso };
    }
  }
  return null;
}

function ownershipStateOf(app: ApplicationItem): OwnershipState {
  if (app.ownership && app.ownership.found && app.ownership.hydrusBoardsBought > 0) return 'board_found';
  if (app.ownershipVerified) return 'verified';
  if (app.proofKind || app.ownershipProof) return 'proof_to_confirm';
  return 'no_board';
}

interface Pending {
  app: ApplicationItem;
  kind: 'approve' | 'deny';
  program?: string;
  note?: string;
  timer: number;
}

export function Applications({ onChange }: { onChange: () => void }) {
  const { data, loading, error, reload } = useAsync(() => apiGet<{ items: ApplicationItem[] }>('/v1/admin/applications'), []);
  const [tab, setTab] = useState<Tab>('needs_review');
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [declineFor, setDeclineFor] = useState<ApplicationItem | null>(null);
  const [proofFor, setProofFor] = useState<ApplicationItem | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  // Commit any still-pending optimistic action when leaving the screen.
  useEffect(() => {
    return () => {
      const p = pendingRef.current;
      if (p) {
        window.clearTimeout(p.timer);
        void commit(p);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const all = data?.items ?? [];
  const hiddenId = pending?.app.id;
  const needsReview = all.filter((a) => a.status === 'received' && a.id !== hiddenId);
  const autoApproved = all.filter((a) => a.status === 'approved' && a.autoApproved);
  const decided = all.filter((a) => a.status === 'denied' || a.status === 'withdrawn' || (a.status === 'approved' && !a.autoApproved));
  const shown = tab === 'needs_review' ? needsReview : tab === 'auto_approved' ? autoApproved : decided;

  async function commit(p: Pending) {
    try {
      if (p.kind === 'approve') await apiPost(`/v1/admin/applications/${p.app.id}/approve`, { program: p.program ?? p.app.program });
      else await apiPost(`/v1/admin/applications/${p.app.id}/deny`, { note: p.note ?? '' });
    } finally {
      reload();
      onChange();
    }
  }

  // Optimistic approve/deny: hide the card, show a 10s Undo toast, fire the API when it expires.
  function startPending(app: ApplicationItem, kind: 'approve' | 'deny', extra: { program?: string; note?: string }) {
    if (pending) {
      window.clearTimeout(pending.timer);
      void commit(pending);
    }
    const timer = window.setTimeout(() => {
      setPending(null);
      setToastMsg(null);
      void commit({ app, kind, ...extra, timer: 0 });
    }, 10000);
    setPending({ app, kind, ...extra, timer });
    setToastMsg(`${COPY.applications.undoToast}${kind === 'approve' ? 'Approved' : 'Declined'} ${app.applicantName}.`);
  }

  function undo() {
    if (pending) window.clearTimeout(pending.timer);
    setPending(null);
    setToastMsg(null);
  }

  async function confirmProof(app: ApplicationItem) {
    setBusy(app.id);
    try {
      await apiPost(`/v1/admin/applications/${app.id}/confirm-proof`, { source: app.proofSource ?? 'other' });
      reload();
      onChange();
      setToastMsg(`${COPY.applications.proofConfirmedToast}${app.applicantName}. You can approve now.`);
    } finally {
      setBusy(null);
    }
  }

  function askForProof() {
    setToastMsg(COPY.applications.askForProofToast);
  }

  return (
    <div>
      <h1 className="screen-title">{COPY.applications.title}</h1>
      <p className="screen-sub">{COPY.applications.sub}</p>

      <div className="toolbar">
        <TabButton active={tab === 'needs_review'} onClick={() => setTab('needs_review')} label={COPY.applications.tabNeedsReview} n={needsReview.length} />
        <TabButton active={tab === 'auto_approved'} onClick={() => setTab('auto_approved')} label={COPY.applications.tabAutoApproved} n={autoApproved.length} />
        <TabButton active={tab === 'decided'} onClick={() => setTab('decided')} label={COPY.applications.tabDecided} n={decided.length} />
      </div>

      {loading ? <Spinner /> : null}
      {error ? <LoadError message={error} onRetry={reload} /> : null}
      {data && shown.length === 0 ? <Empty>{COPY.applications.empty}</Empty> : null}

      {shown.map((app) => (
        <ApplicationCard
          key={app.id}
          app={app}
          tab={tab}
          busy={busy === app.id}
          onApprove={(program) => startPending(app, 'approve', { program })}
          onDeny={() => setDeclineFor(app)}
          onConfirmProof={() => confirmProof(app)}
          onAskForProof={askForProof}
          onViewProof={() => setProofFor(app)}
          onUndoAuto={async () => {
            if (!window.confirm(COPY.applications.undoConfirm)) return;
            setBusy(app.id);
            try {
              await apiPost(`/v1/admin/applications/${app.id}/undo-auto-approve`);
              reload();
              onChange();
            } finally {
              setBusy(null);
            }
          }}
        />
      ))}

      {declineFor ? (
        <DeclineSheet
          app={declineFor}
          onCancel={() => setDeclineFor(null)}
          onConfirm={(note) => {
            const app = declineFor;
            setDeclineFor(null);
            startPending(app, 'deny', { note });
          }}
        />
      ) : null}

      {proofFor ? <ProofViewer app={proofFor} onClose={() => setProofFor(null)} /> : null}

      {toastMsg ? (
        <Toast
          message={toastMsg}
          actionLabel={pending ? COPY.applications.undoAction : undefined}
          onAction={pending ? undo : undefined}
          onClose={() => setToastMsg(null)}
        />
      ) : null}
    </div>
  );
}

function TabButton({ active, onClick, label, n }: { active: boolean; onClick: () => void; label: string; n: number }) {
  return (
    <button className={active ? '' : 'secondary'} onClick={onClick}>
      {label} ({n})
    </button>
  );
}

function OwnershipBlock({ app }: { app: ApplicationItem }) {
  const state = ownershipStateOf(app);
  if (state === 'board_found') {
    const o = qualifyingOrder(app.ownership);
    return (
      <div className="ostate green">
        <span>✓</span>
        <span>
          <strong>{COPY.applications.boardFound}</strong>
          {o ? (
            <div className="small">
              {o.board}, order {o.name}, {shortDate(o.dateIso)}
            </div>
          ) : (
            <div className="small">
              {pluralize(app.ownership?.hydrusBoardsBought ?? 1, 'board')}
              {app.ownership?.firstPurchaseIso ? `, first on ${shortDate(app.ownership.firstPurchaseIso)}` : ''}
            </div>
          )}
          {app.status === 'received' ? <div className="small muted">In review: a web application waiting for a program.</div> : null}
        </span>
      </div>
    );
  }
  if (state === 'verified') {
    return (
      <div className="ostate green">
        <span>✓</span>
        <strong>{COPY.applications.verified}</strong>
      </div>
    );
  }
  if (state === 'proof_to_confirm') {
    return (
      <div className="ostate amber">
        <span>!</span>
        <span>
          <strong>{COPY.applications.proofToConfirm}</strong>
          <div className="small">
            {app.proofKind ?? 'proof'}
            {app.proofSource ? ` (${app.proofSource.replace(/_/g, ' ')})` : ''}
            {app.ownershipProof ? `: ${app.ownershipProof}` : ''}
          </div>
        </span>
      </div>
    );
  }
  return (
    <div className="ostate red">
      <span>✕</span>
      <strong>{COPY.applications.noBoard}</strong>
    </div>
  );
}

function FraudChips({ app }: { app: ApplicationItem }) {
  if (app.fraudSignals.length === 0) return null;
  return (
    <div className="chips" style={{ marginTop: 8 }}>
      {app.fraudSignals.map((s) => (
        <span
          key={s.code}
          className="chip-amber"
          title={FRAUD_EXPLAIN[s.code] ?? s.label}
          onClick={(e) => {
            // Tap shows the explanation inline (hover shows the title on desktop).
            (e.currentTarget as HTMLElement).textContent = FRAUD_EXPLAIN[s.code] ?? s.label;
          }}
        >
          {s.label}
        </span>
      ))}
    </div>
  );
}

function ApplicationCard({
  app,
  tab,
  busy,
  onApprove,
  onDeny,
  onConfirmProof,
  onAskForProof,
  onViewProof,
  onUndoAuto,
}: {
  app: ApplicationItem;
  tab: Tab;
  busy: boolean;
  onApprove: (program: string) => void;
  onDeny: () => void;
  onConfirmProof: () => void;
  onAskForProof: () => void;
  onViewProof: () => void;
  onUndoAuto: () => void;
}) {
  const a = app.answers ?? {};
  const state = ownershipStateOf(app);
  const canApprove = app.ownershipVerified; // the engine also 409-gates this

  return (
    <div className="card">
      <div className="row between" style={{ alignItems: 'flex-start' }}>
        <div className="headline grow">
          <div className="name">{app.applicantName}</div>
          <div className="email">{app.email ? app.email : <span className="muted">{COPY.applications.noEmail}</span>}</div>
          <div className="sub">
            {app.memberAlias ? `“${app.memberAlias}”` : ''}
            {app.memberCrewNumber ? `${app.memberAlias ? ' | ' : ''}Crew #${app.memberCrewNumber}` : ''}
          </div>
        </div>
        <span className="tag">{programLabel(app.program)}</span>
      </div>

      <div style={{ marginTop: 10 }} className="stack">
        <OwnershipBlock app={app} />

        {app.proofKind === 'photo' ? (
          <button className="ghost" onClick={onViewProof} style={{ alignSelf: 'flex-start' }}>
            {COPY.applications.viewProof}
          </button>
        ) : null}

        {typeof a.wherePaddle === 'string' ? (
          <div className="small">
            <span className="muted">Where they paddle: </span>
            {a.wherePaddle}
          </div>
        ) : null}
        {typeof a.whyLoveBoard === 'string' ? (
          <div className="small">
            <span className="muted">Why they love their board: </span>
            {a.whyLoveBoard}
          </div>
        ) : null}

        {app.autoApproveReason ? <div className="small muted">{app.autoApproveReason}</div> : null}
        {app.status === 'denied' && app.decisionNote ? <div className="small muted">Note: {app.decisionNote}</div> : null}

        <FraudChips app={app} />
      </div>

      {tab === 'needs_review' ? (
        <div className="toolbar" style={{ marginTop: 12, marginBottom: 0 }}>
          {state === 'board_found' || state === 'verified' ? (
            <>
              <select defaultValue={app.program} id={`prog-${app.id}`} aria-label="Program to approve into">
                {PROGRAMS.map((p) => (
                  <option key={p} value={p}>
                    {programLabel(p)}
                  </option>
                ))}
              </select>
              <button
                disabled={busy || !canApprove}
                onClick={() => {
                  const sel = document.getElementById(`prog-${app.id}`) as HTMLSelectElement | null;
                  onApprove(sel?.value ?? app.program);
                }}
              >
                {COPY.applications.approve}
              </button>
              <button className="secondary" disabled={busy} onClick={onDeny}>
                {COPY.applications.deny}
              </button>
            </>
          ) : state === 'proof_to_confirm' ? (
            <>
              <button disabled={busy} onClick={onConfirmProof}>
                {COPY.applications.confirmProof}
              </button>
              <button className="secondary" disabled={busy} onClick={onDeny}>
                {COPY.applications.deny}
              </button>
              <span className="small muted" style={{ alignSelf: 'center' }}>
                {COPY.applications.needsProof}
              </span>
            </>
          ) : (
            <>
              <button disabled={busy} onClick={onAskForProof}>
                {COPY.applications.askForProof}
              </button>
              <button className="secondary" disabled={busy} onClick={onDeny}>
                {COPY.applications.deny}
              </button>
            </>
          )}
        </div>
      ) : null}

      {tab === 'auto_approved' ? (
        <div className="toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
          <button className="danger" disabled={busy} onClick={onUndoAuto}>
            {COPY.applications.undo}
          </button>
        </div>
      ) : null}

      {tab === 'decided' ? (
        <div className="row" style={{ gap: 6, marginTop: 8 }}>
          {app.status === 'denied' ? <Pill tone="red">Declined</Pill> : null}
          {app.status === 'withdrawn' ? <Pill tone="gray">Undone</Pill> : null}
          {app.status === 'approved' ? <Pill tone="green">Approved</Pill> : null}
          <span className="small muted">
            {app.reviewer ? `by ${app.reviewer}` : ''} {app.reviewedAtIso ? `on ${shortDate(app.reviewedAtIso)}` : ''}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function DeclineSheet({ app, onCancel, onConfirm }: { app: ApplicationItem; onCancel: () => void; onConfirm: (note: string) => void }) {
  const [note, setNote] = useState('');
  return (
    <Sheet title={COPY.applications.declineTitle} onClose={onCancel}>
      <p className="muted small">{COPY.applications.declineBlurb}</p>
      <input placeholder={COPY.common.note} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="preview">
        <div className="small muted">{COPY.applications.declinePreview}</div>
        <div>{note.trim() ? note.trim() : COPY.applications.declinePreviewText}</div>
      </div>
      <div className="row">
        <button className="danger" onClick={() => onConfirm(note.trim())}>
          {COPY.applications.deny} {app.applicantName}
        </button>
        <button className="secondary" onClick={onCancel}>
          {COPY.common.cancel}
        </button>
      </div>
    </Sheet>
  );
}

function ProofViewer({ app, onClose }: { app: ApplicationItem; onClose: () => void }) {
  const creds = getCreds();
  const src = creds ? `${creds.baseUrl}/v1/admin/applications/${app.id}/proof-photo` : '';
  return (
    <Sheet title={COPY.applications.proofViewerTitle} onClose={onClose}>
      <p className="small muted">
        {app.proofKind}
        {app.proofSource ? ` (${app.proofSource.replace(/_/g, ' ')})` : ''}
      </p>
      {app.proofKind === 'photo' ? (
        // The admin bearer token is sent on XHR/fetch, but an <img> cannot carry it; the
        // proof-photo route is admin-gated, so this renders when the console shares a session
        // cookie at cutover. Locally it shows the link for now.
        <a href={src} target="_blank" rel="noreferrer">
          Open proof photo
        </a>
      ) : (
        <div className="preview">{app.ownershipProof ?? 'No file on record.'}</div>
      )}
    </Sheet>
  );
}
